import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Clock,
  Search,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  FileText,
  TrendingDown,
  TrendingUp,
  MapPin,
  User,
  AlertTriangle,
  Package,
  Loader2,
  Wrench,
  FolderTree,
} from 'lucide-react';
import type { Asset } from '@/types/asset';
import type { HistoryEntry, HistoryFilterType, HistoryGroup } from '@/types/history';
import { dataService } from '@/services/data-service';
import { format, isToday, isYesterday, isThisWeek, isThisMonth, startOfMonth } from 'date-fns';

const DEFAULT_PAGE_SIZE = 25;

interface AssetDetailHistoryProps {
  readonly asset: Asset;
}

export function AssetDetailHistory({ asset }: Readonly<AssetDetailHistoryProps>) {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<HistoryFilterType>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Load history on mount or when filter changes
  useEffect(() => {
    setHistory([]);
    setCurrentPage(1);
    loadHistory(1, true);
  }, [asset.id, filterType]);

  const loadHistory = async (page: number, reset: boolean = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const eventTypeParam = filterType !== 'all' ? getEventTypeParam(filterType) : undefined;
      const response = await dataService.getAssetHistory(asset.id, {
        page,
        pageSize: DEFAULT_PAGE_SIZE,
        eventType: eventTypeParam,
      });
      
      if (reset) {
        setHistory(response.items);
      } else {
        setHistory(prev => [...prev, ...response.items]);
      }
      setTotalCount(response.totalCount);
      setHasMore(response.hasMore);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to load asset history:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const getEventTypeParam = (filter: HistoryFilterType): string | undefined => {
    switch (filter) {
      case 'created':
        return 'created';
      case 'updated':
        return 'updated';
      case 'transactions':
        return 'transaction_added';
      case 'audits':
        return 'audit_completed';
      case 'notes':
        return 'note_added';
      case 'status_changes':
        return 'status_changed';
      case 'location_changes':
        return 'location_changed';
      case 'depreciation':
        return 'depreciation_applied';
      default:
        return undefined;
    }
  };

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadHistory(currentPage + 1, false);
    }
  }, [loadingMore, hasMore, currentPage]);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loadMore]);

  // Filter and search history
  const filteredHistory = useMemo(() => {
    let filtered = [...history];

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter((entry) => {
        switch (filterType) {
          case 'created':
            return entry.eventType === 'created';
          case 'updated':
            return entry.eventType === 'updated';
          case 'transactions':
            return entry.eventType.startsWith('transaction_');
          case 'audits':
            return entry.eventType.startsWith('audit_');
          case 'notes':
            return entry.eventType.startsWith('note_');
          case 'status_changes':
            return entry.eventType === 'status_changed';
          case 'location_changes':
            return entry.eventType === 'location_changed';
          case 'depreciation':
            return entry.eventType === 'depreciation_applied';
          default:
            return true;
        }
      });
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((entry) =>
        entry.title.toLowerCase().includes(query) ||
        entry.description.toLowerCase().includes(query) ||
        entry.userName.toLowerCase().includes(query) ||
        entry.comments?.toLowerCase().includes(query) ||
        entry.noteContent?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [history, filterType, searchQuery]);

  // Group history by date
  const groupedHistory = useMemo(() => {
    const groups: HistoryGroup[] = [];
    const groupMap = new Map<string, HistoryEntry[]>();

    filteredHistory.forEach((entry) => {
      const date = new Date(entry.timestamp);
      let groupLabel: string;

      if (isToday(date)) {
        groupLabel = 'Today';
      } else if (isYesterday(date)) {
        groupLabel = 'Yesterday';
      } else if (isThisWeek(date)) {
        groupLabel = 'This Week';
      } else if (isThisMonth(date)) {
        groupLabel = 'This Month';
      } else {
        groupLabel = format(startOfMonth(date), 'MMMM yyyy');
      }

      if (!groupMap.has(groupLabel)) {
        groupMap.set(groupLabel, []);
      }
      groupMap.get(groupLabel)!.push(entry);
    });

    // Convert map to array and sort entries within each group
    groupMap.forEach((entries, label) => {
      groups.push({
        label,
        entries: [...entries].sort((a, b) => {
          // First, sort by audit number descending if both have audit numbers
          if (a.auditNumber && b.auditNumber) {
            const auditCompare = b.auditNumber.localeCompare(a.auditNumber);
            if (auditCompare !== 0) return auditCompare;
          }
          // If only one has an audit number, prioritize it
          if (a.auditNumber && !b.auditNumber) return -1;
          if (!a.auditNumber && b.auditNumber) return 1;
          // Otherwise, sort by timestamp descending
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        }),
      });
    });

    // Sort groups by most recent first
    const groupOrder = ['Today', 'Yesterday', 'This Week', 'This Month'];
    return groups.sort((a, b) => {
      const aIndex = groupOrder.indexOf(a.label);
      const bIndex = groupOrder.indexOf(b.label);
      
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      // For month groups, sort by date
      return b.label.localeCompare(a.label);
    });
  }, [filteredHistory]);

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'created':
        return <Plus className="h-4 w-4 text-white" />;
      case 'updated':
        return <Edit className="h-4 w-4 text-white" />;
      case 'transaction_added':
        return <Plus className="h-4 w-4 text-white" />;
      case 'transaction_updated':
        return <Edit className="h-4 w-4 text-white" />;
      case 'transaction_deleted':
        return <Trash2 className="h-4 w-4 text-white" />;
      case 'audit_started':
        return <FileText className="h-4 w-4 text-white" />;
      case 'audit_completed':
        return <CheckCircle className="h-4 w-4 text-white" />;
      case 'note_added':
        return <FileText className="h-4 w-4 text-white" />;
      case 'note_updated':
        return <Edit className="h-4 w-4 text-white" />;
      case 'note_deleted':
        return <Trash2 className="h-4 w-4 text-white" />;
      case 'status_changed':
        return <AlertTriangle className="h-4 w-4 text-white" />;
      case 'location_changed':
        return <MapPin className="h-4 w-4 text-white" />;
      case 'assignment_changed':
        return <User className="h-4 w-4 text-white" />;
      case 'depreciation_applied':
        return <TrendingDown className="h-4 w-4 text-white" />;
      case 'revaluation_applied':
        return <TrendingUp className="h-4 w-4 text-white" />;
      case 'disposal_initiated':
        return <AlertTriangle className="h-4 w-4 text-white" />;
      case 'disposal_completed':
        return <CheckCircle className="h-4 w-4 text-white" />;
      case 'maintenance_logged':
        return <Wrench className="h-4 w-4 text-white" />;
      case 'category_changed':
        return <FolderTree className="h-4 w-4 text-white" />;
      case 'owner_changed':
        return <User className="h-4 w-4 text-white" />;
      default:
        return <Package className="h-4 w-4 text-white" />;
    }
  };

  const getEventIconBackground = (eventType: string): string => {
    switch (eventType) {
      case 'created':
      case 'transaction_added':
      case 'audit_completed':
      case 'revaluation_applied':
        return 'bg-green-600';
      case 'updated':
      case 'transaction_updated':
      case 'audit_started':
      case 'note_updated':
        return 'bg-blue-600';
      case 'transaction_deleted':
      case 'note_deleted':
      case 'depreciation_applied':
        return 'bg-red-600';
      case 'note_added':
        return 'bg-purple-600';
      case 'status_changed':
      case 'disposal_initiated':
        return 'bg-orange-600';
      case 'location_changed':
        return 'bg-indigo-600';
      case 'assignment_changed':
      case 'owner_changed':
        return 'bg-cyan-600';
      case 'disposal_completed':
        return 'bg-gray-600';
      case 'maintenance_logged':
        return 'bg-amber-600';
      case 'category_changed':
        return 'bg-violet-600';
      default:
        return 'bg-gray-600';
    }
  };


  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex gap-4">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-48" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={`skeleton-${i}`} className="flex gap-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search history..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterType} onValueChange={(value: string) => setFilterType(value as HistoryFilterType)}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="created">Created</SelectItem>
            <SelectItem value="updated">Updated</SelectItem>
            <SelectItem value="transactions">Transactions</SelectItem>
            <SelectItem value="audits">Audits</SelectItem>
            <SelectItem value="notes">Notes</SelectItem>
            <SelectItem value="status_changes">Status Changes</SelectItem>
            <SelectItem value="location_changes">Location Changes</SelectItem>
            <SelectItem value="depreciation">Depreciation</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* History Timeline */}
      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Asset History
            <Badge variant="secondary" className="ml-auto">
              {totalCount} {totalCount === 1 ? 'event' : 'events'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {groupedHistory.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No History Found</h3>
              <p className="text-muted-foreground">
                {searchQuery || filterType !== 'all'
                  ? 'No events match your filters. Try adjusting your search.'
                  : 'No history events have been recorded for this asset yet.'}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {groupedHistory.map((group) => (
                <div key={group.label}>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-4 uppercase tracking-wide">
                    {group.label}
                  </h3>
                  <div className="space-y-3 relative before:absolute before:left-5 before:top-0 before:bottom-0 before:w-px before:bg-border">
                    {group.entries.map((entry, _index) => (
                      <div key={entry.id} className="relative flex gap-3 pl-0">
                        {/* Timeline dot */}
                        <div className="relative flex-shrink-0">
                          <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-background shadow-sm ${getEventIconBackground(entry.eventType)}`}>
                            {getEventIcon(entry.eventType)}
                          </div>
                        </div>

                        {/* Event content */}
                        <div className="flex-1 pb-4">
                          <div className="rounded-lg border border-border/40 bg-muted/30 p-3 hover:bg-muted/50 transition-colors">
                            <div className="flex items-start justify-between gap-4 mb-1">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                  <h4 className="font-semibold text-sm">{entry.title}</h4>
                                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                                    {format(new Date(entry.timestamp), 'HH:mm')}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground">{entry.description}</p>
                              </div>
                            </div>

                            {/* Field changes */}
                            {entry.fieldChanges && entry.fieldChanges.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {entry.fieldChanges.map((change, idx) => (
                                  <div key={`${entry.id}-change-${idx}`} className="text-xs flex items-center gap-2">
                                    <span className="font-medium">{change.fieldLabel}:</span>
                                    <span className="text-muted-foreground line-through">{change.oldValue}</span>
                                    <span>→</span>
                                    <span className="text-green-600 dark:text-green-400">{change.newValue}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Amount, Audit, Notes, Comments - all in one line if possible */}
                            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                              {entry.amount !== undefined && (
                                <span className="font-medium">
                                  Amount: {entry.currency} {entry.amount.toLocaleString()}
                                </span>
                              )}
                              {(entry.auditNumber || entry.auditName) && (
                                <span>
                                  <span className="font-medium">Audit:</span> {entry.auditNumber || entry.auditName}
                                  {entry.auditNumber && entry.auditName && entry.auditNumber !== entry.auditName && ` - ${entry.auditName}`}
                                </span>
                              )}
                              {entry.noteContent && (
                                <span className="italic">"{entry.noteContent}"</span>
                              )}
                              {entry.comments && (
                                <span>"{entry.comments}"</span>
                              )}
                              {/* User and timestamp */}
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {entry.userName}
                              </span>
                              <span>•</span>
                              <span>{format(new Date(entry.timestamp), 'MMM d, yyyy')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Load more trigger element */}
              <div ref={loadMoreRef} className="py-4">
                {loadingMore && (
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Loading more...</span>
                  </div>
                )}
                {hasMore && !loadingMore && (
                  <div className="flex justify-center">
                    <Button variant="outline" size="sm" onClick={loadMore}>
                      Load More
                    </Button>
                  </div>
                )}
                {!hasMore && history.length > 0 && (
                  <p className="text-center text-sm text-muted-foreground">
                    All {totalCount} events loaded
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
